import { useEffect, useMemo, useState } from "react";
import { api, formatErr } from "../lib/api";
import DashboardShell from "../components/DashboardShell";
import { SAR } from "../components/RiyalIcon";
import { Car, Bike, Search, Plus, Minus, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import ProductSheet from "../components/ProductSheet";
import { DeliveryLocationPicker } from "../components/DeliveryLocationPicker";
import { useBranch } from "../context/BranchContext";
import { MapPin } from "lucide-react";

export default function ManualOrder() {
  const nav = useNavigate();
  const branchCtx = useBranch();
  // Branches the backend already scoped to this user (owner: all; employee: assigned only).
  const branchChoices = useMemo(() => branchCtx?.activeBranches || [], [branchCtx?.activeBranches]);
  const [branchId, setBranchId] = useState(null);
  useEffect(() => {
    if (!branchCtx?.loaded) return;
    const valid = branchChoices.some((b) => b.id === branchCtx.currentId);
    setBranchId(valid ? branchCtx.currentId : (branchChoices[0]?.id || branchCtx.currentId || null));
  }, [branchCtx?.loaded, branchCtx?.currentId, branchChoices]);
  const branchHeaders = branchId ? { headers: { "X-Branch-Id": branchId } } : {};
  const [restaurant, setRestaurant] = useState(null);
  const [products, setProducts] = useState([]);
  const [cats, setCats] = useState([]);
  const [pickup, setPickup] = useState("CAR");
  const [payment, setPayment] = useState("ON_PICKUP");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [carType, setCarType] = useState("");
  const [carColor, setCarColor] = useState("");
  const [carPlate, setCarPlate] = useState("");
  const [address, setAddress] = useState("");
  const [deliveryLatitude, setDeliveryLatitude] = useState(null);
  const [deliveryLongitude, setDeliveryLongitude] = useState(null);
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [customer, setCustomer] = useState(null);
  const [lastOrder, setLastOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [sheetProduct, setSheetProduct] = useState(null);
  const [account, setAccount] = useState(null);

  useEffect(() => {
    (async () => {
      const [c, p, a] = await Promise.all([
        api.get("/categories"),
        api.get("/products"),
        api.get("/account/me").catch(() => ({ data: null })),
      ]);
      setCats(c.data); setProducts(p.data); setAccount(a.data);
    })();
  }, []);

  // Branch-specific settings (pickup options, delivery fee) follow the selected branch.
  useEffect(() => {
    if (!branchCtx?.loaded) return;
    api.get("/restaurants/mine", branchHeaders).then((r) => {
      setRestaurant(r.data);
      setPickup((cur) => ((cur === "CAR" && !r.data.car_pickup_enabled) || (cur === "DELIVERY" && !r.data.delivery_enabled)) ? "STORE" : cur);
    }).catch((e) => toast.error(formatErr(e)));
  }, [branchId, branchCtx?.loaded]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!/^05\d{8}$/.test(phone)) { setCustomer(null); setLastOrder(null); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/customers/lookup?phone=${phone}`);
        if (data.found) {
          setCustomer(data.customer); setLastOrder(data.last_order);
          setName((prev) => prev || data.customer.name || "");
        }
        else { setCustomer(null); setLastOrder(null); }
      } catch {}
    }, 350);
    return () => clearTimeout(t);
  }, [phone]);

  const filtered = useMemo(() =>
    products.filter(p => p.available && (!q || p.name.includes(q))).slice(0, 30),
    [products, q]
  );

  const addProd = (p) => {
    // Products with option groups must go through the sheet so required options are chosen.
    if ((p.option_groups || []).length > 0) { setSheetProduct(p); return; }
    setItems(prev => {
      const idx = prev.findIndex(i => i._key === p.id);
      if (idx >= 0) { const copy = [...prev]; copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 }; return copy; }
      return [...prev, {
        _key: p.id, product_id: p.id, name: p.name, price: Number(p.price),
        quantity: 1, selected_addons: [], addon_snapshot: [], unit_extra: 0,
      }];
    });
  };

  // Called from ProductSheet after a customization is confirmed.
  const addCustomized = ({ product, quantity, selected_addons, addon_snapshot, unit_extra }) => {
    setItems(prev => {
      const key = product.id + JSON.stringify(selected_addons);
      const idx = prev.findIndex(i => i._key === key);
      if (idx >= 0) { const copy = [...prev]; copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity }; return copy; }
      return [...prev, {
        _key: key, product_id: product.id, name: product.name, price: Number(product.price),
        quantity, selected_addons, addon_snapshot: addon_snapshot || [], unit_extra: Number(unit_extra || 0),
      }];
    });
    setSheetProduct(null);
  };

  const changeQty = (key, d) => setItems(prev =>
    prev.flatMap(i => i._key === key ? (i.quantity + d <= 0 ? [] : [{ ...i, quantity: i.quantity + d }]) : [i])
  );

  const reorder = () => {
    if (!lastOrder) return;
    setItems(lastOrder.items.map(i => ({
      _key: i.product_id + JSON.stringify(i.selected_addons || []),
      product_id: i.product_id, name: i.name, price: Number(i.price),
      quantity: i.quantity,
      selected_addons: i.selected_addons || [],
      addon_snapshot: i.addons || [],
      unit_extra: Number(i.unit_extra || 0),
    })));
    setPickup(lastOrder.pickup_method);
    if (lastOrder.car_type) { setCarType(lastOrder.car_type); setCarColor(lastOrder.car_color); setCarPlate(lastOrder.car_plate); }
    if (lastOrder.delivery_address) setAddress(lastOrder.delivery_address);
    if (
      lastOrder.pickup_method === "DELIVERY"
      && Number.isFinite(lastOrder.delivery_latitude)
      && Number.isFinite(lastOrder.delivery_longitude)
    ) {
      setDeliveryLatitude(lastOrder.delivery_latitude);
      setDeliveryLongitude(lastOrder.delivery_longitude);
    } else {
      setDeliveryLatitude(null);
      setDeliveryLongitude(null);
    }
    toast.success("تم تحميل آخر طلب");
  };

  const subtotal = items.reduce((s, i) => s + (i.price + Number(i.unit_extra || 0)) * i.quantity, 0);
  const delivery = pickup === "DELIVERY" ? Number(restaurant?.delivery_fee || 0) : 0;
  const hasDeliveryLocation = Number.isFinite(deliveryLatitude)
    && Number.isFinite(deliveryLongitude);

  const submit = async () => {
    if (!/^05\d{8}$/.test(phone)) return toast.error("رقم الجوال غير صحيح");
    if (items.length === 0) return toast.error("أضف صنف واحد على الأقل");
    if (pickup === "CAR" && (!carType || !carColor || !carPlate)) return toast.error("أكمل بيانات السيارة");
    if (pickup === "DELIVERY" && !address) return toast.error("أدخل العنوان");
    if (pickup === "DELIVERY" && !hasDeliveryLocation) {
      return toast.error("حدد موقع التوصيل من الخريطة");
    }
    setBusy(true);
    try {
      const payload = {
        restaurant_id: restaurant.id, source: "PHONE",
        pickup_method: pickup, payment_method: payment,
        customer_name: name, customer_phone: phone,
        car_type: carType, car_color: carColor, car_plate: carPlate,
        delivery_address: address,
        delivery_latitude: pickup === "DELIVERY"
          ? deliveryLatitude
          : undefined,
        delivery_longitude: pickup === "DELIVERY"
          ? deliveryLongitude
          : undefined,
        items: items.map(i => ({ product_id: i.product_id, quantity: i.quantity, selected_addons: i.selected_addons || [] })),
      };
      await api.post("/orders/manual", payload, branchHeaders);
      toast.success("تم إنشاء الطلب");
      nav("/dashboard");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(false); }
  };

  return (
    <DashboardShell title="إضافة طلب" restaurantName={restaurant?.name}>
      {branchChoices.length > 1 && (
        <label className="mb-3 flex items-center gap-2 rounded-2xl border border-orange-100 bg-white px-3 py-2 text-sm font-bold" data-testid="mo-branch-selector">
          <MapPin size={16} className="text-orange-600" />
          <span className="text-gray-500">الفرع:</span>
          <select
            value={branchId || ""}
            onChange={(e) => setBranchId(e.target.value)}
            className="flex-1 bg-transparent outline-none font-extrabold text-orange-800"
            data-testid="mo-branch-select"
          >
            {branchChoices.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        </label>
      )}
      {/* Pickup */}
      <div className={`grid gap-2 mb-3 ${restaurant?.car_pickup_enabled && restaurant?.delivery_enabled ? "grid-cols-3" : (restaurant?.car_pickup_enabled || restaurant?.delivery_enabled) ? "grid-cols-2" : "grid-cols-1"}`}>
        {restaurant?.car_pickup_enabled && (
          <button onClick={() => setPickup("CAR")} className={`rounded-2xl border-2 p-3 font-bold flex flex-col items-center gap-1 ${pickup === "CAR" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`} data-testid="mo-pickup-car"><Car size={20}/><span>سيارة</span></button>
        )}
        <button onClick={() => setPickup("STORE")} className={`rounded-2xl border-2 p-3 font-bold flex flex-col items-center gap-1 ${pickup === "STORE" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`} data-testid="mo-pickup-store"><span>🏬</span><span>استلام من الفرع</span></button>
        {restaurant?.delivery_enabled && (
          <button onClick={() => setPickup("DELIVERY")} className={`rounded-2xl border-2 p-3 font-bold flex flex-col items-center gap-1 ${pickup === "DELIVERY" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`} data-testid="mo-pickup-delivery"><Bike size={20}/><span>توصيل</span></button>
        )}
      </div>

      {/* Customer */}
      <div className="bg-white rounded-2xl border border-orange-100 p-3 space-y-2">
        <input placeholder="رقم الجوال" value={phone} onChange={e => setPhone(e.target.value)} data-testid="mo-phone" className="w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500" />
        {customer && (
          <div className="rounded-2xl bg-orange-50 p-3 text-sm">
            <div className="font-bold">{customer.name || "بدون اسم"} · <span className="num-badge">{customer.orders_count}</span> طلب سابق</div>
            {lastOrder && <button onClick={reorder} data-testid="mo-reorder" className="mt-2 pill bg-white border border-orange-200 text-orange-700 font-bold px-3 py-1.5 text-xs inline-flex items-center gap-1"><RotateCcw size={14}/> إعادة آخر طلب</button>}
          </div>
        )}
        <input placeholder="اسم العميل" value={name} onChange={e => setName(e.target.value)} data-testid="mo-name" className="w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500" />
        {pickup === "CAR" && (
          <div className="grid grid-cols-3 gap-2">
            <input placeholder="السيارة" value={carType} onChange={e => setCarType(e.target.value)} data-testid="mo-cartype" className="h-12 rounded-2xl bg-gray-50 border border-gray-200 px-3 focus:border-orange-500 outline-none" />
            <input placeholder="اللون" value={carColor} onChange={e => setCarColor(e.target.value)} data-testid="mo-carcolor" className="h-12 rounded-2xl bg-gray-50 border border-gray-200 px-3 focus:border-orange-500 outline-none" />
            <input placeholder="اللوحة" value={carPlate} onChange={e => setCarPlate(e.target.value)} data-testid="mo-carplate" className="h-12 rounded-2xl bg-gray-50 border border-gray-200 px-3 focus:border-orange-500 outline-none" />
          </div>
        )}
        {pickup === "DELIVERY" && (
          <>
            <DeliveryLocationPicker
              latitude={deliveryLatitude}
              longitude={deliveryLongitude}
              onLocationChange={({ latitude, longitude }) => {
                setDeliveryLatitude(latitude);
                setDeliveryLongitude(longitude);
              }}
            />
            <input
              placeholder="عنوان التوصيل"
              value={address}
              onChange={e => setAddress(e.target.value)}
              data-testid="mo-address"
              className="w-full h-12 rounded-2xl bg-gray-50 border border-gray-200 px-4 outline-none focus:border-orange-500"
            />
          </>
        )}
      </div>

      {/* Products */}
      <div className="mt-4">
        <div className="relative">
          <Search className="absolute end-3 top-3.5 text-gray-400" size={18} />
          <input placeholder="ابحث عن صنف…" value={q} onChange={e => setQ(e.target.value)} data-testid="mo-search"
            className="w-full h-12 rounded-2xl bg-white border border-gray-200 pe-10 ps-4 outline-none focus:border-orange-500" />
        </div>
        <div className="grid grid-cols-2 gap-2 mt-3">
          {filtered.map(p => (
            <button key={p.id} onClick={() => addProd(p)} data-testid={`mo-prod-${p.id}`} className="bg-white rounded-2xl border border-orange-100 p-2 text-start hover:border-orange-300">
              <div className="w-full h-20 rounded-xl food-img overflow-hidden mb-2">{p.image_url && <img src={p.image_url} alt="" className="w-full h-full object-cover" />}</div>
              <div className="font-bold text-sm truncate">{p.name}</div>
              <div className="flex items-center justify-between mt-0.5">
                <div className="text-orange-600 font-bold text-xs"><SAR>{p.price}</SAR></div>
                {(p.option_groups || []).length > 0 && <div className="text-[10px] text-gray-500">خيارات</div>}
              </div>
            </button>
          ))}
        </div>
      </div>

      {/* Selected items */}
      {items.length > 0 && (
        <div className="mt-4 bg-white rounded-2xl border border-orange-100 p-3 space-y-2">
          {items.map(i => (
            <div key={i._key} className="flex items-start gap-2" data-testid={`mo-item-${i.product_id}`}>
              <div className="flex-1">
                <div className="font-bold text-sm">{i.name}</div>
                {(i.addon_snapshot || []).length > 0 && (
                  <div className="text-[11px] text-gray-500 mt-0.5 space-y-0.5">
                    {i.addon_snapshot.map((a, ai) => (
                      <div key={`${a.option_id || a.name}-${ai}`}>
                        + {a.name}
                        {a.price_delta !== 0 && (
                          <span className="text-orange-600">
                            {" "}({a.price_delta > 0 ? "+" : "−"}
                            {" "}<SAR>{Math.abs(a.price_delta)}</SAR>)
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <button onClick={() => changeQty(i._key, -1)} className="w-8 h-8 rounded-full border grid place-items-center"><Minus size={14}/></button>
              <span className="num-badge font-extrabold w-6 text-center">{i.quantity}</span>
              <button onClick={() => changeQty(i._key, 1)} className="w-8 h-8 rounded-full brand-grad text-white grid place-items-center"><Plus size={14}/></button>
              <div className="w-16 text-end text-sm num-badge"><SAR>{(i.price + Number(i.unit_extra || 0)) * i.quantity}</SAR></div>
            </div>
          ))}
        </div>
      )}

      {/* Payment */}
      <div className="mt-4 grid grid-cols-2 gap-2">
        <button onClick={() => setPayment("ON_PICKUP")} className={`rounded-2xl border-2 p-3 font-bold text-sm ${payment === "ON_PICKUP" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`} data-testid="mo-pay-cash">💵 عند الاستلام</button>
        <button onClick={() => setPayment("PREPAID")} className={`rounded-2xl border-2 p-3 font-bold text-sm ${payment === "PREPAID" ? "border-orange-500 bg-orange-50 text-orange-700" : "border-gray-200 bg-white"}`} data-testid="mo-pay-prepaid">💳 مدفوع مسبقاً</button>
      </div>

      <div className="sticky bottom-0 -mx-4 mt-4 bg-white border-t border-orange-100 p-4">
        <div className="flex items-center justify-between mb-3">
          <span className="text-gray-600 text-sm">الإجمالي</span>
          <span className="text-xl font-extrabold text-orange-600"><SAR>{subtotal + delivery}</SAR></span>
        </div>
        <button disabled={busy || account?.is_expired} onClick={submit} className="w-full pill brand-grad text-white font-bold py-3.5 tap-target disabled:opacity-60" data-testid="mo-confirm">{busy ? "جاري…" : "تأكيد الطلب"}</button>
        {account?.is_expired && (
          <div className="mt-2 rounded-2xl border border-red-200 bg-red-50 text-red-700 text-xs font-bold p-2" data-testid="mo-expired-warn">
            انتهت صلاحية الحساب — لا يمكن إنشاء طلبات جديدة. يرجى التواصل مع الإدارة لتجديد الاشتراك.
          </div>
        )}
      </div>

      {sheetProduct && (
        <ProductSheet
          product={sheetProduct}
          onClose={() => setSheetProduct(null)}
          onAdd={addCustomized}
        />
      )}
    </DashboardShell>
  );
}
